import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../Task/CSS/task.css";
import { Ripple } from "primereact/ripple";
import { confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Tooltip } from "primereact/tooltip";
import { Column } from "primereact/column";
import {
  Dropdown,
  DropdownChangeEvent,
  DropdownProps,
} from "primereact/dropdown";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import { Dialog } from "primereact/dialog";

import NoDataCreated from "../../Components/NoDataCreated";
import ErrorPage from "../../Components/ErrorPage";
import PageLoader from "../../Components/PageLoader";
import DatePicker from "../../Components/DatePicker";
import Textarea from "../../Components/TextArea";
import TextInput from "../../Components/TextInput";
import { goToTop } from "../../Components/goToTop";
import { formatDate, parseStringToDate } from "../../Utils/getDate";
import {
  getUserValuableTaskSummaryApi,
  taskPartChangeStatusApi,
  taskPartCreateApi,
  taskPartDeleteApi,
  taskPartGetDataApi,
  taskPartGetTaskContactsApi,
  taskPartUpdateApi,
} from "../../Api/Task/TaskApi";
import {
  AssignContact,
  CreateTaskArray,
  Tasks,
} from "../../Controllers/Types/Task/TaskInterface";
import { checkText } from "../../Utils/Validator";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { ContactSubtype } from "../../Utils/appConstant";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";
import NumberInput from "../../Components/NumberInput";
import { InputNumberChangeEvent } from "primereact/inputnumber";
import { fetchAllContact } from "../../Controllers/Thunks/contactThunks";
import { setStatus } from "../../Controllers/Slices/contactSlice";
import { AnimatePresence, motion } from "framer-motion";
import {
  AssignTaskDialog,
  CarryForwardDialog,
  CompleteTaskDialog,
  CompletedEvidenceDialog,
  FollowUpDialog,
  isAlreadyCarryForwardRequestedForCurrentDate,
  TaskDetailsDialog,
} from "../Components/Task/TaskActionDialogs";

interface Priority {
  name: string;
  colorCode: string;
}

const priorities: Priority[] = [
  {
    name: "High",
    colorCode: "#EF9A9A",
  },
  {
    name: "Medium",
    colorCode: "#FFEFBC",
  },
  {
    name: "Low",
    colorCode: "#B9F6CA",
  },
];

interface TaskStatusOption {
  name: "Pending" | "Ongoing" | "Completed";
  icon: string;
}

const taskStatusOptions: TaskStatusOption[] = [
  {
    name: "Pending",
    icon: "bi bi-hourglass-split",
  },
  {
    name: "Ongoing",
    icon: "bi bi-clock-history",
  },
  {
    name: "Completed",
    icon: "bi bi-check-circle-fill",
  },
];

const Task: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const today = new Date();
  const toast = useRef<Toast>(null);

  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const error = "";

  const [dataState, setDataState] = useState<"Add" | "Edit">("Add");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  const [tasksData, setTasksData] = useState<Tasks[]>([]);
  const [filteredTasksData, setFilteredTasksData] = useState<Tasks[]>([]);

  const [priorityFilter, setPriorityFilter] = useState<Priority | null>(null);
  const [assignFilter, setAssignFilter] = useState<"All" | "Assigned" | "Created">("All");
  const [contactFilter, setContactFilter] = useState<AssignContact | null>(null);
  const [statusFilter, setStatusFilter] = useState<TaskStatusOption | null>(null);
  const [taskContacts, setTaskContacts] = useState<AssignContact[]>([]);
  const [statusLoadingTaskId, setStatusLoadingTaskId] = useState<number | string | null>(null);

  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<Nullable<Date>>(today);
  const [selectedDate, setSelectedDate] = useState<string>("");

  const [showTaskModal, setShowTaskModal] = useState<boolean>(false);
  const [taskName, setTaskName] = useState<string>("");
  const [taskDueDate, setTaskDueDate] = useState<Nullable<Date>>(today);
  const [taskDescription, setTaskDescription] = useState<string>("");
  const [taskPriority, setTaskPriority] = useState<Priority | null>(null);
  const [projectReference, setProjectReference] = useState<string>("");
  const [contacts, setContacts] = useState<AssignContact[]>([]);
  const [taskAssignContacts, setTaskAssignContacts] =useState<AssignContact | null>(null);
  const [selectedTask, setSelectedTask] = useState<Tasks | null>(null);
  const [showAddedTaskList, setShowAddedTaskList] = useState<boolean>(false);
  const [taskHr, setTaskHr] = useState<number | null>(0);
  const [taskMin, setTaskMin] = useState<number | null>(0);

  const [jobNoError, setJobNoError] = useState<string>("");
  const [dueDateError, setDueDateError] = useState<string>("");
  const [priorityError, setPriorityError] = useState<string>("");
  const [assignContactError, setAssignContactError] = useState<string>("");
  const [projectReferenceError, setProjectReferenceError] =useState<string>("");
  const [taskNameError, setTaskNameError] = useState<string>("");
  const [descriptionError, setDescriptionError] = useState<string>("");
  const [taskHrError, setTaskHrError] = useState<string>("");
  const [taskMinError, setTaskMinError] = useState<string>("");

  const [showTaskViewModal, setShowTaskViewModal] = useState<boolean>(false);
  const [selectedTaskData, setSelectedTaskData] = useState<Tasks | null>(null);

  const [completeTaskData, setCompleteTaskData] = useState<Tasks | null>(null);
  const [followUpTaskData, setFollowUpTaskData] = useState<Tasks | null>(null);
  const [carryForwardTaskData, setCarryForwardTaskData] =useState<Tasks | null>(null);
  const [assignTaskData, setAssignTaskData] = useState<Tasks | null>(null);
  const [evidenceTaskData, setEvidenceTaskData] = useState<Tasks | null>(null);

  const [showCompleteTaskDialog, setShowCompleteTaskDialog] = useState(false);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [showCarryForwardDialog, setShowCarryForwardDialog] = useState(false);
  const [showAssignTaskDialog, setShowAssignTaskDialog] = useState(false);
  const [showEvidenceDialog, setShowEvidenceDialog] = useState(false);

  const [taskSummaryLoading, setTaskSummaryLoading] = useState(false);
  const [taskSummaryData, setTaskSummaryData] = useState<any>(null);

  const [createTaskArray, setCreateTaskArray] = useState<CreateTaskArray[]>([]);
  const { loading, employees, professionals } = useAppSelector((state) => state.contact);

  const [displayTotalTime, setDisplayTotalTime] = useState<string>("");
  const [addTaskDisable, setAddTaskDisable] = useState<boolean>(false);
  const [showMoreOption, setShowMoreOption] = useState<boolean>(false);

  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>hasPermission(state, screen, permission));};

  const isPermissionAddTask = useCanEdit("Task", "add_task");
  const isPermissionEditTask = useCanEdit("Task", "edit_task");
  const isPermissionDeleteTask = useCanEdit("Task", "delete_task");
  const isPermissionDetailsView = useCanEdit("Task", "details_view");
  const isPermissionTaskCompleted = useCanEdit("Task", "task_completed");

  useEffect(() => {
    taskDataGet();
  }, []);

  useEffect(() => {
    dispatch(fetchAllContact());
    dispatch(setStatus(1));
  }, [dispatch]);

  useEffect(() => {
    const employeeList = employees
      .filter((emp) => emp.contactSubType !== "Temporary Employee")
      .map((emp) => {
        const assignContact: AssignContact = {
          id: emp.id,
          first_name: emp.first_name,
          last_name: emp.last_name,
          profile_image: emp.profile_image,
          role: emp.role_name,
          by: "Employee",
        };

        return assignContact;
      });

    const professionalList: AssignContact[] = [];

    for (const emp of professionals) {
      if (emp.contactSubType === ContactSubtype.Company && emp.subContact) {
        for (const sub of emp.subContact) {
          if (sub.employee_type === "Main Employee") {
            const assignContact: AssignContact = {
              id: sub.id,
              first_name: sub.first_name,
              last_name: sub.last_name,
              profile_image: sub.profile_image,
              role: sub.role_name,
              by: "Company",
            };

            professionalList.push(assignContact);
          }
        }
      } else {
        const assignContact: AssignContact = {
          id: emp.id,
          first_name: emp.first_name,
          last_name: emp.last_name,
          profile_image: emp.profile_image,
          role: emp.role_name,
          by: "Self Employee",
        };

        professionalList.push(assignContact);
      }
    }

    setContacts([...employeeList, ...professionalList]);
  }, [employees, professionals]);

  useEffect(() => {
    checkText(projectReference, false, setProjectReferenceError);
  }, [projectReference]);

  useEffect(() => {
    const taskDueDateString = formatDate(taskDueDate || null);
    checkText(taskDueDateString, false, setDueDateError);
  }, [taskDueDate]);

  useEffect(() => {
    checkText(taskName, false, setTaskNameError);
  }, [taskName]);

  useEffect(() => {
    let hrs = 0;
    let mins = 0;

    hrs = createTaskArray.reduce((sum, e) => sum + Number(e.hours ?? 0), 0);
    mins = createTaskArray.reduce((sum, e) => sum + Number(e.minute ?? 0), 0);

    hrs += Math.floor(mins / 60);
    mins = mins % 60;

    const totalTimeString = `${hrs} hrs ${mins} mins`;
    const totalMinutes = hrs * 60 + mins;

    setDisplayTotalTime(totalTimeString);
    setAddTaskDisable(totalMinutes > 600);
  }, [createTaskArray]);

  useEffect(() => {
    setDateFilter(today);
    setSelectedDate(formatDate(today));
  }, []);

  useEffect(() => {
    taskContactsGet(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    applyFilters();
  }, [
    selectedDate,
    selectedPriority,
    tasksData,
    assignFilter,
    contactFilter,
    statusFilter,
  ]);

  useEffect(() => {
    const dateValue = formatDate(taskDueDate || null);

    if (taskAssignContacts && dateValue) {
      fetchTaskSummaryForForm(taskAssignContacts, dateValue);
    } else {
      setTaskSummaryData(null);
    }
  }, [taskAssignContacts, taskDueDate]);

  const showToast = (message: {
    severity: "success" | "error" | "warn" | "info";
    summary: string;
    detail: string;
  }) => {
    toast.current?.show({
      severity: message.severity,
      summary: message.summary,
      detail: message.detail,
      life: 3000,
    });
  };

  const taskDataGet = async () => {
    setLoadingPage(true);

    const data = await taskPartGetDataApi();

    setLoadingPage(false);

    if (data.status) {
      const list = data.data || [];

      setTasksData(list);
      setFilteredTasksData(list);

      if (selectedTaskData) {
        const updatedSelected =
          list.find((task) => task.task_id === selectedTaskData.task_id) ??
          selectedTaskData;

        setSelectedTaskData(updatedSelected);
      }
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: data.massage ?? "Unable to load task data.",
      });
    }
  };

  const taskContactsGet = async (dateValue: string) => {
    if (!dateValue) {
      setTaskContacts([]);
      setContactFilter(null);
      return;
    }

    const response = await taskPartGetTaskContactsApi(dateValue);

    const success =
      response?.statusCode === 200 ||
      response?.status === true ;

    if (success) {
      const list = response?.data ?? [];

      setTaskContacts(list);

      if (contactFilter) {
        const contactExists = list.some(
          (contact) => contact.id === contactFilter.id && contact.by === contactFilter.by
        );

        if (!contactExists) {
          setContactFilter(null);
        }
      }

      return;
    }

    setTaskContacts([]);
    setContactFilter(null);
  };

  const normalizeStatus = (value?: string | null) => {
    const status = String(value ?? "").trim().toLowerCase();

    if (status === "completed") return "Completed";
    if (status === "ongoing") return "Ongoing";
    if (status === "pending") return "Pending";

    return "";
  };

  const getTaskDateValue = (task?: Tasks | null) => {
    return String((task as any)?.task_date ?? "").trim();
  };

  const getTaskMainStatus = (task: Tasks): "Pending" | "Ongoing" | "Completed" => {
    const modelStatus = normalizeStatus((task as any)?.status);

    if (modelStatus === "Completed") return "Completed";
    if (modelStatus === "Ongoing") return "Ongoing";
    if (modelStatus === "Pending") return "Pending";

    if (task.is_completed === 1) return "Completed";

    return "Ongoing";
  };

  const isTaskCompleted = (task: Tasks) => {
    return getTaskMainStatus(task) === "Completed" || task.is_completed === 1;
  };

  const canEditTask = (task: Tasks) => {
    if (!isPermissionEditTask) return false;
    if (task.assign_by !== null) return false;
    if (isTaskCompleted(task)) return false;
    if (isAlreadyCarryForwardRequestedForCurrentDate(task)) return false;

    return true;
  };

  /* -------------------------------
     Summary + OT Validation Helpers
  -------------------------------- */

  const parseTimeToMinutes = (value: any): number => {
    if (value === null || value === undefined || value === "") return 0;

    if (typeof value === "number") {
      return Math.round(value * 60);
    }

    const text = String(value).trim();

    if (!text) return 0;

    if (text.includes(":")) {
      const parts = text.split(":");
      const h = Number(parts[0] ?? 0);
      const m = Number(parts[1] ?? 0);

      if (Number.isNaN(h) || Number.isNaN(m)) return 0;

      return h * 60 + m;
    }

    const hourMatch = text.match(/(\d+(\.\d+)?)\s*(h|hr|hrs|hour)/i);
    const minMatch = text.match(/(\d+)\s*(m|min|mins|minute)/i);

    if (hourMatch || minMatch) {
      const h = hourMatch ? Number(hourMatch[1]) : 0;
      const m = minMatch ? Number(minMatch[1]) : 0;

      return Math.round(h * 60 + m);
    }

    const numeric = Number(text);

    if (Number.isNaN(numeric)) return 0;

    return Math.round(numeric * 60);
  };

  const getSummaryPayload = (summary: any) => {
    if (!summary) return null;

    if (summary?.data && typeof summary.data === "object") return summary.data;

    return summary;
  };

  const getDayTotalMinutes = (summary: any) => {
    const data = getSummaryPayload(summary);

    if (!data) return 8 * 60;

    const value =
      data?.dayWiseTotalWorkHours ??
      data?.day_wise_total_work_hours ??
      data?.day_total_work_hours ??
      data?.total_work_hours ??
      data?.totalHours ??
      data?.total_hours ??
      data?.working_hours ??
      data?.work_hours ??
      null;

    const minutes = parseTimeToMinutes(value);

    return minutes > 0 ? minutes : 8 * 60;
  };

  const getAlreadyAssignedMinutes = (summary: any) => {
    const data = getSummaryPayload(summary);

    if (!data) return 0;

    const value =
      data?.totalTaskHours ??
      data?.total_task_hours ??
      data?.assignedTaskHours ??
      data?.assigned_task_hours ??
      data?.usedTaskHours ??
      data?.used_task_hours ??
      data?.used_hours ??
      data?.assigned_hours ??
      null;

    const minutes = parseTimeToMinutes(value);

    return minutes < 0 ? 0 : minutes;
  };

  const getSelectedTaskMinutes = () => {
    return Number(taskHr ?? 0) * 60 + Number(taskMin ?? 0);
  };

  const getOldSelectedTaskMinutes = () => {
    if (!selectedTask) return 0;

    const currentDate = formatDate(taskDueDate || null);
    const oldDate = getTaskDateValue(selectedTask);

    const sameDate = currentDate === oldDate;

    const oldContact = selectedTask.assign?.[0];

    const sameContact =
      oldContact &&
      taskAssignContacts &&
      oldContact.id === taskAssignContacts.id &&
      oldContact.by === taskAssignContacts.by;

    if (dataState === "Edit" && sameDate && sameContact) {
      return (
        Number(selectedTask.hours ?? 0) * 60 + Number(selectedTask.minute ?? 0)
      );
    }

    return 0;
  };

  const getAvailableBeforeCurrentTask = () => {
    const total = getDayTotalMinutes(taskSummaryData);
    let alreadyAssigned = getAlreadyAssignedMinutes(taskSummaryData);

    const oldMinutes = getOldSelectedTaskMinutes();

    if (oldMinutes > 0) {
      alreadyAssigned -= oldMinutes;
    }

    if (alreadyAssigned < 0) alreadyAssigned = 0;

    const available = total - alreadyAssigned;

    return available < 0 ? 0 : available;
  };

  const formatMinutesToHourText = (totalMinutes: number | null) => {
    if (totalMinutes === null) return "---";

    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const isTaskTimeOverAvailable = () => {
    if (!taskSummaryData || !taskAssignContacts) return false;

    const selectedMinutes = getSelectedTaskMinutes();
    const availableMinutes = getAvailableBeforeCurrentTask();

    return selectedMinutes > availableMinutes;
  };

  const getEditDisabledReason = (task: Tasks) => {
    if (isTaskCompleted(task)) return "Completed task cannot be edited.";

    if (isAlreadyCarryForwardRequestedForCurrentDate(task)) {
      return "Carry forwarded task cannot be edited.";
    }

    if (task.assign_by !== null) {
      return "Assigned by another user, edit disabled.";
    }

    if (!isPermissionEditTask) {
      return "You do not have permission to edit this task.";
    }

    return "";
  };

  const fetchTaskSummaryForForm = async (
    contact: AssignContact | null,
    dateValue: string = formatDate(taskDueDate || null)
  ) => {
    if (!contact) {
      setTaskSummaryData(null);
      return;
    }

    if (!dateValue) {
      setTaskSummaryData(null);

      showToast({
        severity: "warn",
        summary: "Date required",
        detail: "Please select due date to check task summary.",
      });

      return;
    }

    const input = {
      id: contact.id,
      by: contact.by,
      date: dateValue,
    };

    setTaskSummaryLoading(true);

    const response = await getUserValuableTaskSummaryApi(input);

    setTaskSummaryLoading(false);

    const success =
      response?.statusCode === 200 ||
      response?.status === true 
    ;

    if (success) {
      setTaskSummaryData(response?.data ?? response);
    } else {
      setTaskSummaryData(null);

      showToast({
        severity: "error",
        summary: "Error",
        detail:
          response?.massage ??
         
          "Unable to get task summary.",
      });
    }
  };

  const renderFormSummary = () => {
    if (taskSummaryLoading) {
      return (
        <div className="task_summary_box">
          <div className="task_summary_header">
            <i className="bi bi-hourglass-split"></i>
            <span>Checking task summary...</span>
          </div>
        </div>
      );
    }

    if (!taskSummaryData || !taskAssignContacts) return null;

    const data = getSummaryPayload(taskSummaryData);

    const message =
      data?.message ??
      data?.massage ??
      taskSummaryData?.message ??
      taskSummaryData?.massage ??
      "";

    const totalDayMinutes = getDayTotalMinutes(taskSummaryData);
    const alreadyAssignedMinutes = getAlreadyAssignedMinutes(taskSummaryData);
    const availableMinutes = getAvailableBeforeCurrentTask();
    const selectedMinutes = getSelectedTaskMinutes();
    const isOver = isTaskTimeOverAvailable();

    return (
      <div className={`task_summary_box ${isOver ? "has_warning" : ""}`}>
        <div className="task_summary_header">
          <i
            className={
              isOver ? "bi bi-exclamation-triangle" : "bi bi-clipboard2-data"
            }
          ></i>
          <span>{isOver ? "OT Warning" : "Task Summary"}</span>
        </div>

        {message && <p className="task_summary_message">{message}</p>}

        <div className="task_summary_grid">
          <div className="task_summary_item">
            <span>Day Total</span>
            <h6>{formatMinutesToHourText(totalDayMinutes)}</h6>
          </div>

          <div className="task_summary_item">
            <span>Assigned</span>
            <h6>{formatMinutesToHourText(alreadyAssignedMinutes)}</h6>
          </div>

          <div className="task_summary_item">
            <span>Available</span>
            <h6>{formatMinutesToHourText(availableMinutes)}</h6>
          </div>

          <div className="task_summary_item">
            <span>Selected</span>
            <h6>{formatMinutesToHourText(selectedMinutes)}</h6>
          </div>
        </div>

        {isOver && (
          <div className="task_summary_warning">
            <i className="bi bi-exclamation-circle"></i>
            <span>
              Only {formatMinutesToHourText(availableMinutes)} available for
              this user. Selected time is higher than available time.
            </span>
          </div>
        )}
      </div>
    );
  };

  const handleHrChange = (e: InputNumberChangeEvent) => {
    const newVal = e.value ?? 0;
    setTaskHr(newVal);
    setTaskHrError("");
    setTaskMinError("");
  };

  const handleMinChange = (e: InputNumberChangeEvent) => {
    const newVal = e.value ?? 0;
    setTaskMin(newVal);
    setTaskHrError("");
    setTaskMinError("");
  };

function checkCredentials(): boolean {
  const taskDueDateString = formatDate(taskDueDate || null);

  const projectRefe = checkText(
    projectReference,
    true,
    setProjectReferenceError
  );

  const taskDueDateStringValid = checkText(
    taskDueDateString,
    true,
    setDueDateError
  );

  const taskNameValid = checkText(taskName, true, setTaskNameError);

  const taskPriorityValid = checkText(
    taskPriority?.name || "",
    true,
    setPriorityError
  );

  const taskDescriptionValid = checkText(
    taskDescription,
    false,
    setDescriptionError
  );

  const hr = Number(taskHr ?? 0);
  const min = Number(taskMin ?? 0);

  let timeValid = true;

  // default clear
  setTaskHrError("");
  setTaskMinError("");

  // Hr / Min basic range validation only if user entered any time OR assign selected
  const hasEnteredTime = hr > 0 || min > 0;
  const hasAssignContact = taskAssignContacts !== null;

  if (hasEnteredTime || hasAssignContact) {
    if (hr < 0 || hr > 23) {
      setTaskHrError("Enter valid hours");
      timeValid = false;
    }

    if (min < 0 || min > 59) {
      setTaskMinError("Enter valid minutes");
      timeValid = false;
    }
  }

  const enteredMinutes = getSelectedTaskMinutes();

  // IMPORTANT:
  // Assign contact select pannina mattum Hr/Min required
  if (hasAssignContact && enteredMinutes <= 0) {
    setTaskMinError("Please enter task hours or minutes");
    timeValid = false;
  }

  // Assign contact select pannina mattum summary check
  if (hasAssignContact && taskSummaryLoading) {
    setTaskMinError("Please wait, checking available hours");
    timeValid = false;
  }

  if (hasAssignContact && !taskSummaryData) {
    setTaskMinError("User task summary not found");
    timeValid = false;
  }

  // Assign contact select pannina mattum OT validation
  if (hasAssignContact && taskSummaryData && enteredMinutes > 0) {
    const availableMinutes = getAvailableBeforeCurrentTask();

    if (enteredMinutes > availableMinutes) {
      setTaskMinError(
        `Only ${formatMinutesToHourText(
          availableMinutes
        )} available for this user`
      );

      showToast({
        severity: "warn",
        summary: "OT Warning",
        detail: `Only ${formatMinutesToHourText(
          availableMinutes
        )} available for this user.`,
      });

      timeValid = false;
    }
  }

  return (
    projectRefe &&
    taskDueDateStringValid &&
    taskPriorityValid &&
    taskNameValid &&
    taskDescriptionValid &&
    timeValid
  );
}

  function showAPIErrorData(keyName: string, error: string): void {
    if (keyName === "project_program_task_library_id") {
      setTaskNameError(error);
    }

    if (keyName === "project_reference") {
      setProjectReferenceError(error);
    }

    if (keyName === "task_name") {
      setTaskNameError(error);
    }

    if (keyName === "deu_date") {
      setDueDateError(error);
    }

    if (keyName === "priority") {
      setPriorityError(error);
    }
  }

  const handlePriorityChange = (e: DropdownChangeEvent) => {
    const selectedPriority = e.value as Priority | null;

    setSelectedPriority(selectedPriority?.name || null);
    setPriorityFilter(selectedPriority);
  };

  const handleAssignChange = (e: DropdownChangeEvent) => {
    const selectedFilter = e.value as "Assigned" | "Created" | "All";
    setAssignFilter(selectedFilter);
  };

  const handleContactFilterChange = (e: DropdownChangeEvent) => {
    setContactFilter(e.value as AssignContact | null);
  };

  const handleStatusFilterChange = (e: DropdownChangeEvent) => {
    setStatusFilter(e.value as TaskStatusOption | null);
  };

  const handleDateChange = (e: any) => {
    const selectedDate = e.value;
    const formattedDate = formatDate(selectedDate);

    setDateFilter(selectedDate || null);
    setSelectedDate(formattedDate);
    setContactFilter(null);
  };

  const applyFilters = () => {
    let filteredData = [...tasksData];

    if (selectedPriority) {
      filteredData = filteredData.filter(
        (task) => task?.priority === selectedPriority
      );
    }

    if (selectedDate) {
      filteredData = filteredData.filter(
        (task) => getTaskDateValue(task) === selectedDate
      );
    }

    if (assignFilter === "Assigned") {
      filteredData = filteredData.filter((task) => task?.assign_by !== null);
    } else if (assignFilter === "Created") {
      filteredData = filteredData.filter((task) => task?.assign_by === null);
    }

    if (contactFilter) {
      filteredData = filteredData.filter((task) =>
        task.assign?.some(
          (contact) =>
            contact.id === contactFilter.id && contact.by === contactFilter.by
        )
      );
    }

    if (statusFilter) {
      filteredData = filteredData.filter(
        (task) => getTaskMainStatus(task) === statusFilter.name
      );
    }

    setFilteredTasksData(filteredData);
  };

  const getStatusLabel = (rowData: Tasks) => {
    return getTaskMainStatus(rowData);
  };

  const getPriorityColor = (priority: string) => {
    if (priority === "High") return "#EF9A9A";
    if (priority === "Medium") return "#FFEFBC";
    if (priority === "Low") return "#B9F6CA";
    return "#FFEFBC";
  };

  const assignedContactBodyTemplate = (rowData: Tasks) => {
    const taskContacts = rowData?.assign;

    return (
      <React.Fragment>
        {rowData.assign_by !== null ? (
          <React.Fragment>
            <Tooltip target={`#tooltip_assignby_contact_${rowData.task_id}`} />

            <div
              key={rowData.task_id}
              id={`tooltip_assignby_contact_${rowData.task_id}`}
              className="task_contact_display m-auto"
              data-pr-tooltip={`Assigned by: ${
                rowData.assign_by?.first_name +
                " " +
                rowData.assign_by?.last_name
              }`}
              data-pr-position="top"
            >
              {rowData.assign_by?.profile_image ? (
                <img src={rowData.assign_by?.profile_image} alt="" />
              ) : (
                <span>
                  {rowData.assign_by?.first_name?.[0]}
                  {rowData.assign_by?.last_name?.[0]}
                </span>
              )}
            </div>
          </React.Fragment>
        ) : taskContacts?.length > 0 ? (
          <div
            className="project_card_contact_area justify-content-center mt-0 p-0"
            onClick={() => {
              if (canEditTask(rowData)) {
                openAssignTaskPopup(rowData);
              }
            }}
            style={{ cursor: canEditTask(rowData) ? "pointer" : "default" }}
          >
            {taskContacts.slice(0, 3).map((contact, index) => {
              const contactTooltipId = `tooltip_contact_${rowData.task_id}_${index}`;

              return (
                <React.Fragment key={`${contact.id}_${index}`}>
                  <Tooltip target={`#${contactTooltipId}`} />

                  <div
                    id={contactTooltipId}
                    className="task_contact_display"
                    data-pr-tooltip={`${contact.first_name} ${contact.last_name}`}
                    data-pr-position="top"
                  >
                    {contact.profile_image ? (
                      <img
                        src={contact.profile_image}
                        alt={`${contact.first_name} ${contact.last_name}`}
                      />
                    ) : (
                      <span>
                        {contact.first_name?.[0]}
                        {contact.last_name?.[0]}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {taskContacts.length > 3 && (
              <h6
                className="project_card_contact_has_more custom-tooltip"
                data-pr-tooltip={`${taskContacts.length - 3} more`}
                data-pr-position="top"
              >
                +{taskContacts.length - 3}
              </h6>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="task_assign_plus_btn p-ripple"
            onClick={() => openAssignTaskPopup(rowData)}
          >
            <i className="bi bi-plus-lg"></i>
            <Ripple />
          </button>
        )}
      </React.Fragment>
    );
  };

  const priorityBodyTemplate = (rowData: Tasks) => {
    return (
      <div className="flex_center">
        <span
          className="priority_label"
          style={{ backgroundColor: getPriorityColor(rowData.priority) }}
        >
          {rowData.priority}
        </span>
      </div>
    );
  };

  const getStatusClass = (status: string) => {
    return status
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const getStatusIconClass = (status: string) => {
    if (status === "Completed") return "bi bi-check-circle-fill";
    if (status === "Ongoing") return "bi bi-clock-history";
    return "bi bi-hourglass-split";
  };

  const statusValueTemplate = (option: TaskStatusOption, rowData: Tasks) => {
    const currentStatus = getTaskMainStatus(rowData);
    const statusClass = getStatusClass(currentStatus);
    const isLoading = statusLoadingTaskId === rowData.task_id;

    return (
      <button
        type="button"
        className={`task_status_btn status_${statusClass} p-ripple`}
      >
        <i
          className={
            isLoading ? "bi bi-arrow-repeat task_status_spin" : getStatusIconClass(currentStatus)
          }
        ></i>
        <span>{currentStatus}</span>
        <i className="bi bi-chevron-down ms-1"></i>
        <Ripple />
      </button>
    );
  };

  const statusOptionTemplate = (option: TaskStatusOption) => {
    const statusClass = getStatusClass(option.name);

    return (
      <div className={`task_status_option status_${statusClass}`}>
        <i className={option.icon}></i>
        <span>{option.name}</span>
      </div>
    );
  };

  const changeTaskStatus = async (
    task: Tasks,
    status: "Pending" | "Ongoing"
  ) => {
    if (!isPermissionEditTask) {
      showToast({
        severity: "warn",
        summary: "Permission denied",
        detail: "You do not have permission to change task status.",
      });
      return;
    }

    if (isTaskCompleted(task)) {
      showToast({
        severity: "warn",
        summary: "Action blocked",
        detail: "Completed task status cannot be changed here.",
      });
      return;
    }

    setStatusLoadingTaskId(task.task_id);

    const response = await taskPartChangeStatusApi(task.task_id, status);

    setStatusLoadingTaskId(null);

    const success =
      response?.statusCode === 200 ||
      response?.status === true;

    if (success) {
      await taskDataGet();

      showToast({
        severity: "success",
        summary: "Success",
        detail: `Task status changed to ${status}.`,
      });
    } else {
      showToast({
        severity: "error",
        summary: "Error",
        detail: response?.massage ?? "Unable to change task status.",
      });
    }
  };

  const handleStatusChange = (task: Tasks, option: TaskStatusOption | null) => {
    if (!option) return;

    if (option.name === "Completed") {
      if (!isPermissionTaskCompleted) {
        showToast({
          severity: "warn",
          summary: "Permission denied",
          detail: "You do not have permission to complete task.",
        });
        return;
      }

      openCompleteTaskPopup(task);
      return;
    }

    if (getTaskMainStatus(task) === option.name) return;

    changeTaskStatus(task, option.name);
  };

  const statusBodyTemplate = (rowData: Tasks) => {
    const currentStatus = getTaskMainStatus(rowData);

    return (
      <div className="flex_center">
        <Dropdown
          value={taskStatusOptions.find((item) => item.name === currentStatus)}
          onChange={(e: DropdownChangeEvent) =>
            handleStatusChange(rowData, e.value as TaskStatusOption | null)
          }
          options={taskStatusOptions}
          optionLabel="name"
          valueTemplate={(option) => statusValueTemplate(option, rowData)}
          itemTemplate={statusOptionTemplate}
          panelClassName="task_status_option_panel"
          className="task_status_dropdown"
          disabled={statusLoadingTaskId === rowData.task_id}
        />
      </div>
    );
  };

  const tableActionBody = (rowData: Tasks) => {
    const disabledReason = getEditDisabledReason(rowData);
    const editDisabled = disabledReason !== "";
    const tooltipId = `edit_disabled_${rowData.task_id}`;

    return (
      <div className="table_action_btn_area">
        {isPermissionDetailsView && (
          <button
            className="table_action_btn p-ripple"
            onClick={() => handleViewTask(rowData)}
          >
            View
            <Ripple />
          </button>
        )}

        <Tooltip target={`#${tooltipId}`} />

        <span
          id={tooltipId}
          data-pr-tooltip={editDisabled ? disabledReason : ""}
          data-pr-position="top"
        >
          <button
            className={`table_action_btn p-ripple ${
              editDisabled ? "disabled_action_btn" : ""
            }`}
            onClick={() => {
              if (!editDisabled) {
                handleEditTask(rowData);
              }
            }}
            disabled={editDisabled}
          >
            Edit
            <Ripple />
          </button>
        </span>
      </div>
    );
  };

  const openCompleteTaskPopup = (task: Tasks) => {
    if (isTaskCompleted(task)) {
      showToast({
        severity: "warn",
        summary: "Action blocked",
        detail: "This task is already completed.",
      });
      return;
    }

    if (isAlreadyCarryForwardRequestedForCurrentDate(task)) {
      showToast({
        severity: "warn",
        summary: "Action blocked",
        detail: "This task has already been carried forward.",
      });
      return;
    }

    setCompleteTaskData(task);
    setShowCompleteTaskDialog(true);
  };

  const openFollowUpPopup = (task: Tasks) => {
    if (isTaskCompleted(task)) return;
    if (isAlreadyCarryForwardRequestedForCurrentDate(task)) return;
    if ((task.followup_data ?? []).length > 0) return;

    setFollowUpTaskData(task);
    setShowFollowUpDialog(true);
  };

  const openCarryForwardPopup = (task: Tasks) => {
    if (isTaskCompleted(task)) return;
    if (isAlreadyCarryForwardRequestedForCurrentDate(task)) return;
    if ((task.followup_data ?? []).length > 0) return;
    if (task.priority === "High") return;

    setCarryForwardTaskData(task);
    setShowCarryForwardDialog(true);
  };

  const openAssignTaskPopup = (task: Tasks) => {
    if (isTaskCompleted(task)) {
      showToast({
        severity: "warn",
        summary: "Action blocked",
        detail: "Completed task cannot be assigned.",
      });
      return;
    }

    if (isAlreadyCarryForwardRequestedForCurrentDate(task)) {
      showToast({
        severity: "warn",
        summary: "Action blocked",
        detail: "Carried forward task cannot be assigned.",
      });
      return;
    }

    setAssignTaskData(task);
    setShowAssignTaskDialog(true);
  };

  const handleActionSuccess = async (message: string) => {
    setShowCompleteTaskDialog(false);
    setShowFollowUpDialog(false);
    setShowCarryForwardDialog(false);
    setShowAssignTaskDialog(false);
    setShowEvidenceDialog(false);

    setCompleteTaskData(null);
    setFollowUpTaskData(null);
    setCarryForwardTaskData(null);
    setAssignTaskData(null);
    setEvidenceTaskData(null);

    await taskDataGet();

    showToast({
      severity: "success",
      summary: "Success",
      detail: message,
    });
  };

  const handleClearFields = () => {
    setTaskName("");
    setTaskDescription("");
    setTaskDueDate(today);
    setTaskAssignContacts(null);
    setTaskHr(0);
    setTaskMin(0);
    setTaskSummaryData(null);
  };

  const handleClearFieldsMultiTask = () => {
    setTaskName("");
    setTaskDescription("");
    setTaskHr(0);
    setTaskMin(0);
    setTaskAssignContacts(null);
    setTaskSummaryData(null);
  };

  const handleClearError = () => {
    setJobNoError("");
    setDueDateError("");
    setPriorityError("");
    setAssignContactError("");
    setTaskNameError("");
    setDescriptionError("");
    setTaskHrError("");
    setTaskMinError("");
    setProjectReferenceError("");
  };

  const handleAddNewTask = () => {
    setDataState("Add");
    setShowTaskModal(true);
    setShowMoreOption(false);
    setSelectedTask(null);
    handleClearFields();
    handleClearError();
    setTaskPriority(priorities[1]);
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();

    if (createTaskArray.length > 0) {
      postTaskList(createTaskArray);
      return;
    }

    const taskDueDateString = formatDate(taskDueDate || null);
    const assignContact: AssignContact[] = [];

    if (taskAssignContacts !== null) {
      assignContact[0] = taskAssignContacts;
    }

    if (checkCredentials()) {
      const makeArray: CreateTaskArray = {
        created_token: `token-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        task_name: taskName,
        description: taskDescription,
        deu_date: taskDueDateString,
        priority: taskPriority?.name,
        hours: taskHr!,
        minute: taskMin!,
        assign: assignContact,
      };

      postTaskList([makeArray]);
    }
  };

  const postTaskList = async (taskArray: CreateTaskArray[]) => {
    if (taskArray.length === 0) {
      showToast({
        severity: "error",
        summary: "Error creating task.",
        detail: "Please add task before save.",
      });
      return;
    }

    setIsLoading(true);

    const data = await taskPartCreateApi(projectReference, taskArray);

    setIsLoading(false);

    if (data.statusCode === 200 || data.status === true) {
      taskDataGet();
      handleConfirmCloseTaskModal();

      setTimeout(() => {
        setSuccessMessage("Task created successfully.");
        setShowAlert(true);

        setTimeout(() => {
          setSuccessMessage("");
          setShowAlert(false);
        }, 2000);
      }, 500);

      setCreateTaskArray([]);
      setShowAddedTaskList(false);
      handleClearFields();
      setProjectReference("");
    } else if (data.statusCode === 422) {
      Object.entries(data.errors ?? {}).map(([key, value]: any) =>
        showAPIErrorData(key, value[0])
      );
    } else {
      showToast({
        severity: "error",
        summary: "Error creating task.",
        detail: data.massage ??  "There was an error creating the task.",
      });
    }
  };

  const handleAddTask = () => {
    const taskDueDateString = formatDate(taskDueDate || null);

    if (checkCredentials()) {
      const assignlist: AssignContact[] = [];

      if (taskAssignContacts !== null) {
        assignlist[0] = taskAssignContacts;
      }

      const makeArray: CreateTaskArray = {
        created_token: `token-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        task_name: taskName,
        description: taskDescription,
        deu_date: taskDueDateString,
        priority: taskPriority?.name,
        hours: taskHr!,
        minute: taskMin!,
        assign: assignlist,
      };

      setCreateTaskArray([...createTaskArray, makeArray]);
      setShowAddedTaskList(true);
      handleClearFieldsMultiTask();
    }
  };

  const handleDeleteAddedTask = (indexToRemove: number) => {
    setCreateTaskArray((prevArray) => {
      const updatedArray = prevArray.filter(
        (_, index) => index !== indexToRemove
      );

      if (updatedArray.length === 0) {
        setShowAddedTaskList(false);
      }

      return updatedArray;
    });
  };

  const handleEditTask = (data: Tasks) => {
    const disabledReason = getEditDisabledReason(data);

    if (disabledReason) {
      showToast({
        severity: "warn",
        summary: "Edit disabled",
        detail: disabledReason,
      });
      return;
    }

    setShowTaskViewModal(false);
    setShowTaskModal(true);
    setDataState("Edit");
    setShowAddedTaskList(false);
    setSelectedTask(data);
    setShowMoreOption(true);
    setTaskSummaryData(null);

    setTaskDueDate(parseStringToDate(getTaskDateValue(data)));
    setProjectReference(data?.project_reference || "");
    setTaskHr(data?.hours ?? 0);
    setTaskMin(data.minute ?? 0);

    setTaskPriority(
      priorities.filter((priority) => priority.name === data?.priority)[0] ??
        priorities[1]
    );

    const assignedContact = contacts.filter((con) =>
      data.assign.some(
        (assigned) => assigned.id === con.id && assigned.by === con.by
      )
    );

    setTaskAssignContacts(assignedContact[0] || null);

    if (assignedContact[0]) {
      fetchTaskSummaryForForm(assignedContact[0], getTaskDateValue(data));
    }

    setTaskName(data?.task_name);
    setTaskDescription(data?.description || "");
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();

    const taskDueDateString = formatDate(taskDueDate || null);

    if (checkCredentials()) {
      const assignlist: AssignContact[] = [];

      if (taskAssignContacts !== null) {
        assignlist[0] = taskAssignContacts;
      }

      setIsLoading(true);

      const data = await taskPartUpdateApi(
        selectedTask?.task_id || null,
        projectReference || null,
        taskName,
        taskHr!,
        taskMin!,
        taskDescription,
        taskDueDateString,
        taskPriority?.name || "",
        assignlist
      );

      setIsLoading(false);

      if (data.statusCode === 200 || data.status === true) {
        taskDataGet();
        handleCloseTaskModal();

        setTimeout(() => {
          setSuccessMessage("Task updated successfully.");
          setShowAlert(true);

          setTimeout(() => {
            setSuccessMessage("");
            setShowAlert(false);
          }, 2000);
        }, 500);

        setCreateTaskArray([]);
        setShowAddedTaskList(false);
        handleClearFields();
        setProjectReference("");
      } else if (data.statusCode === 422) {
        Object.entries(data.errors ?? {}).map(([key, value]: any) =>
          showAPIErrorData(key, value[0])
        );
      } else {
        showToast({
          severity: "error",
          summary: "Error updating task.",
          detail: data.massage ??  "There was an error updating the task.",
        });
      }
    }
  };

  const handleCloseTaskModal = () => {
    if (createTaskArray.length > 0) {
      confirmDialog({
        message:
          "When you close the add task, the tasks already added will be deleted, are you sure you want to exit?",
        header: "Are you sure you want to exit?",
        icon: "bi bi-info-circle",
        defaultFocus: "accept",
        acceptClassName: "p-button-danger",
        accept: () => handleConfirmCloseTaskModal(),
      });
    } else {
      handleConfirmCloseTaskModal();
    }
  };

  const handleConfirmCloseTaskModal = () => {
    setShowTaskModal(false);
    setCreateTaskArray([]);
    setProjectReference("");
    setTaskSummaryData(null);
  };

  const handleDeleteTask = (): void => {
    confirmDialog({
      message: "Are you sure you want to delete the task detail?",
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteTask(),
    });
  };

  const deleteTask = async () => {
    const data = await taskPartDeleteApi(selectedTask?.task_id || null);

    if (data.statusCode === 200 || data.status === true) {
      taskDataGet();
      setShowTaskModal(false);

      setTimeout(() => {
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Task detail deleted successfully.",
        });
      }, 500);
    } else {
      showToast({
        severity: "error",
        summary: "Error deleting task.",
        detail: data.massage ??  "There was an error deleting the task.",
      });
    }
  };

  const handleViewTask = (data: Tasks) => {
    setShowTaskViewModal(true);
    setSelectedTaskData(data);
  };

  const handleCloseTaskViewModal = () => {
    setShowTaskViewModal(false);
  };

  const contactOptionsTemplate = (option: AssignContact) => {
    return (
      <div className="contact_option_group">
        <div className="contact_profile">
          {option?.profile_image ? (
            <img
              src={option.profile_image}
              alt={`${option.first_name} ${option.last_name}`}
            />
          ) : (
            <span>
            
              {option?.first_name?.[0]}
              {option?.last_name?.[0]}
            </span>
          )}
        </div>

        <div className="contact_details">
          <h6>
            {option?.first_name || option?.last_name
              ? `${option?.first_name ?? ""} ${option?.last_name ?? ""}`.trim()
              : ""}

                
          </h6>
          <small>{option?.role}</small>
        </div>
      </div>
    );
  };

  const selectedContactTemplate = (
    option: AssignContact,
    props: DropdownProps
  ) => {
    if (option) {
      return (
        <div className="d-flex align-items-center overflow-hidden">
          <span>
            {option.first_name} {option.last_name}
          </span>
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
  };

  const priorityOptionTemplate = (option: Priority) => {
    return (
      <div className="d-flex align-items-center w-100 gap-4 justify-content-between">
        <div>{option.name}</div>
        <div
          className="color_display"
          style={{ backgroundColor: option.colorCode }}
        ></div>
      </div>
    );
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const changeMoreView = () => {
    setShowMoreOption(!showMoreOption);
  };

  const goBack = () => {
    navigate(-1);
    goToTop();
  };

  const taskModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5 text-white mb-0">
        {dataState}&nbsp;task
      </h5>

      {dataState === "Edit" ? (
        isPermissionDeleteTask ? (
          <button
            type="button"
            className="close_modal_btn p-ripple"
            onClick={() => handleDeleteTask()}
          >
            <i className="bi bi-trash3"></i>
            <Ripple />
          </button>
        ) : null
      ) : (
        <button
          type="button"
          className="close_modal_btn p-ripple"
          onClick={handleCloseTaskModal}
        >
          <i className="bi bi-x-circle"></i>
          <Ripple />
        </button>
      )}
    </div>
  );

  const taskModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseTaskModal}
      />

      <Button
        label={
          isLoading ? "Processing" : dataState === "Add" ? "Save" : "Update"
        }
        onClick={dataState === "Add" ? handleCreateTask : handleUpdateTask}
        loading={isLoading}
        disabled={addTaskDisable}
        className="custom_btn primary"
      />
    </div>
  );

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {loadingPage || loading ? (
          <PageLoader />
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="main_heading">Task</h4>

              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_header_area has_multi_dropdown mt-sm-3 mt-3">
              <div className="filter_option_group">
                <div className="filter_date_input_group">
                  <Calendar
                    id="date"
                    value={dateFilter}
                    onChange={handleDateChange}
                    dateFormat="dd M yy"
                    placeholder="Select date"
                    className="custom_date_input filter_date_input custom_width"
                    showIcon
                    iconPos="left"
                    icon="bi bi-calendar2-minus-fill"
                    readOnlyInput
                  />

                  {dateFilter && (
                    <i
                      className="bi bi-x date_clear_icon"
                      onClick={() => {
                        setDateFilter(null);
                        setSelectedDate("");
                        setContactFilter(null);
                        setTaskContacts([]);
                      }}
                    ></i>
                  )}
                </div>

                <div className="filter_dropdown_group">
                  <Dropdown
                    id="priority"
                    value={priorityFilter}
                    onChange={handlePriorityChange}
                    options={priorities}
                    optionLabel="name"
                    itemTemplate={priorityOptionTemplate}
                    panelClassName="status_option_panel"
                    placeholder="Priority"
                    className="form_dropdown filter_dropdown"
                    showClear
                  />

                  <Dropdown
                    id="task"
                    value={assignFilter}
                    onChange={handleAssignChange}
                    options={["All", "Assigned", "Created"]}
                    panelClassName="status_option_panel"
                    placeholder="Task"
                    className="form_dropdown filter_dropdown"
                  />

                  <Dropdown
                    id="contactFilter"
                    value={contactFilter}
                    onChange={handleContactFilterChange}
                    options={taskContacts}
                    optionLabel="first_name"
                    itemTemplate={contactOptionsTemplate}
                    valueTemplate={selectedContactTemplate}
                    panelClassName="status_option_panel"
                    placeholder="Contact"
                    className="form_dropdown filter_dropdown"
                    filter
                    filterPlaceholder="Search by name"
                    filterBy="first_name,last_name,role"
                    emptyFilterMessage="No contact(s) found!"
                    showClear
                  />

                  <Dropdown
                    id="statusFilter"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    options={taskStatusOptions}
                    optionLabel="name"
                    itemTemplate={statusOptionTemplate}
                    panelClassName="status_option_panel"
                    placeholder="Status"
                    className="form_dropdown filter_dropdown"
                    showClear
                  />
                </div>
              </div>

              {isPermissionAddTask && (
                <div className="page_action_area">
                  <button
                    className="add_data_link_btn p-ripple"
                    onClick={handleAddNewTask}
                  >
                    <i className="bi bi-plus-circle"></i>
                    <span>Add new task</span>
                    <Ripple />
                  </button>
                </div>
              )}
            </div>

            <div className="page_main_content_section">
              {tasksData?.length > 0 ? (
                <DataTable
                  value={filteredTasksData}
                  paginator
                  size="small"
                  rows={10}
                  emptyMessage="No task(s) found!"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  tableStyle={{ minWidth: "50rem" }}
                  paginatorDropdownAppendTo={"self"}
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="page_table p-0 p-sm-2"
                  rowHover
                  loading={loadingPage}
                  scrollable
                >
                  <Column
                    header="Task name"
                    headerClassName="text_purple"
                    field="task_name"
                    body={(rowData: Tasks) => (
                      <span className="text-capitalize text_bold">
                        {rowData?.task_name ? rowData?.task_name : "---"}
                      </span>
                    )}
                    style={{ width: "25%" }}
                  />

                  <Column
                    header="Reference"
                    headerClassName="text_purple"
                    field="project_reference"
                    body={(rowData: Tasks) => (
                      <span className="text-capitalize">
                        {rowData?.project_reference
                          ? rowData?.project_reference
                          : "---"}
                      </span>
                    )}
                    style={{ width: "22%" }}
                  />

                  <Column
                    header="Task Date"
                    headerClassName="text_purple"
                    field="task_date"
                    body={(rowData: Tasks) => (
                      <span className="text_no_wrap">
                        {getTaskDateValue(rowData) || "---"}
                      </span>
                    )}
                    style={{ width: "10%" }}
                  />

                  <Column
                    header="Assign"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    field="assign_by"
                    body={assignedContactBodyTemplate}
                    style={{ width: "10%" }}
                  />

                  <Column
                    header="Priority"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    field="priority"
                    body={priorityBodyTemplate}
                    style={{ width: "8%" }}
                  />

                  <Column
                    header="Status"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    field="is_completed"
                    body={statusBodyTemplate}
                    style={{ width: "12%" }}
                  />

                  <Column
                    alignHeader="center"
                    body={tableActionBody}
                    className="table_btn_area"
                    frozen
                    alignFrozen="right"
                  />
                </DataTable>
              ) : (
                <NoDataCreated message="No tasks have been added yet" />
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog
        visible={showTaskModal}
        header={taskModalHeader}
        footer={taskModalFooter}
        headerClassName="custom_modal_header"
        className={`custom_modal_dialog ${
          showAddedTaskList ? "modal_dialog_lg" : "modal_dialog_md"
        }`}
        contentClassName={`${dataState === "Add" && "pb-sm-0"}`}
        onHide={handleCloseTaskModal}
        dismissableMask
      >
        <div className="custom_modal_body pb-sm-0">
          <div
            className={`p-1 ps-sm-3 pe-sm-3 w-100 ${
              dataState === "Add" && "pb-sm-0"
            }`}
          >
            <div className="row flex_col_reverse">
              <div
                className={`col-12 ${
                  showAddedTaskList && "col-lg-8 pe-lg-4"
                }`}
              >
                <div
                  className={`row ${
                    showAddedTaskList && "pt-3 pt-sm-4 pt-lg-0"
                  }`}
                >
                  <div className="col-12">
                    <TextInput
                      id="projectReference"
                      label="Project Reference"
                      labelHtmlFor="projectReference"
                      required={true}
                      value={projectReference}
                      placeholder="Project Reference"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setProjectReference(e.target.value)
                      }
                      error={projectReferenceError}
                    />
                  </div>

                  <div className="col-12">
                    <TextInput
                      id="taskName"
                      label="Task name"
                      labelHtmlFor="taskName"
                      required={true}
                      value={taskName}
                      placeholder="Task name"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTaskName(e.target.value)
                      }
                      error={taskNameError}
                    />
                  </div>

                  <div className="col-12 col-sm-6">
                    <DatePicker
                      id="dueDate"
                      label="Due date"
                      labelHtmlFor="dueDate"
                      required={true}
                      value={taskDueDate}
                      placeholder="dd/mm/yyyy"
                      onChange={(date: Nullable<Date>) => {
                        setTaskDueDate(date);

                        const dateValue = formatDate(date || null);

                        if (taskAssignContacts && dateValue) {
                          fetchTaskSummaryForForm(
                            taskAssignContacts,
                            dateValue
                          );
                        } else {
                          setTaskSummaryData(null);
                        }
                      }}
                      error={dueDateError}
                      showClear
                      onClear={() => {
                        setTaskDueDate(null);
                        setTaskSummaryData(null);
                      }}
                    />
                  </div>

                  <div className="col-12 col-sm-6">
                    <div className="page_form_group">
                      <label
                        htmlFor="priority"
                        className="custom_form_label is_required"
                      >
                        Priority
                      </label>

                      <Dropdown
                        id="priority"
                        value={taskPriority}
                        onChange={(e: DropdownChangeEvent) =>
                          setTaskPriority(e.value)
                        }
                        options={priorities}
                        optionLabel="name"
                        itemTemplate={priorityOptionTemplate}
                        placeholder="Select"
                        className="form_dropdown w-100"
                        showClear
                      />

                      {priorityError && (
                        <small className="form_error_msg">
                          {priorityError}
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="show_more_area" onClick={changeMoreView}>
                    <h6>More details add</h6>
                    <i
                      className={`bi bi-chevron-down ${
                        showMoreOption ? "expanded" : ""
                      }`}
                    ></i>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {showMoreOption && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="row">
                        <div className="col-12 col-sm-6">
                          <div className="page_form_group task_assign_full_width_group">
                            <label
                              htmlFor="assignedContact"
                              className="custom_form_label"
                            >
                              Assign
                            </label>

                            <Dropdown
                              id="assignedContact"
                              value={taskAssignContacts}
                              onChange={(e: DropdownChangeEvent) => {
                                setTaskAssignContacts(e.value);
                                setAssignContactError("");
                                setTaskMinError("");

                                const dateValue = formatDate(
                                  taskDueDate || null
                                );

                                if (e.value && dateValue) {
                                  fetchTaskSummaryForForm(e.value, dateValue);
                                } else {
                                  setTaskSummaryData(null);
                                }
                              }}
                              options={contacts}
                              optionLabel="first_name"
                              itemTemplate={contactOptionsTemplate}
                              valueTemplate={selectedContactTemplate}
                              placeholder="Assign"
                              filter
                              filterInputAutoFocus={true}
                              filterPlaceholder="Search by name.."
                              filterBy="first_name,last_name,role"
                              className="form_dropdown w-100"
                              emptyFilterMessage="No contact(s) found!"
                              showClear
                            />

                            {assignContactError && (
                              <small className="form_error_msg">
                                {assignContactError}
                              </small>
                            )}

                           
                          </div>
                        </div>

                        <div className="col-12 col-sm-3">
                          <NumberInput
                            key="hr"
                            inputId="hr"
                            label="Hr"
                            labelHtmlFor="hr"
                            placeholder="Enter Hr"
                            onChange={handleHrChange}
                            value={taskHr}
                            required={false}
                            minFractionDigits={0}
                            maxFractionDigits={0}
                            min={0}
                            max={23}
                            error={taskHrError}
                          />
                        </div>



                        <div className="col-12 col-sm-3">
                          <NumberInput
                            key="min"
                            inputId="min"
                            label="Min"
                            labelHtmlFor="min"
                            placeholder="Enter Min"
                            onChange={handleMinChange}
                            value={taskMin}
                            required={false}
                            minFractionDigits={0}
                            maxFractionDigits={0}
                            min={0}
                            max={59}
                            error={taskMinError}
                          />
                        </div>

                         <div className="col-12">
                           <div className="task_summary_full_width">
                              {renderFormSummary()}
                            </div>

                         </div>

                        <div className="col-12">
                          <Textarea
                            id="taskDescription"
                            label="Description/note"
                            labelHtmlFor="taskDescription"
                            required={false}
                            value={taskDescription}
                            placeholder="Description"
                            onChange={(
                              e: React.ChangeEvent<HTMLTextAreaElement>
                            ) => setTaskDescription(e.target.value)}
                            error={descriptionError}
                            formGroupClassName="mb-0"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {dataState === "Add" && (
                  <React.Fragment>
                    <div className="flex_end w-100 mt-4 add_task_btn_area">
                      <button
                        className="add_data_btn p-ripple"
                        onClick={handleAddTask}
                      >
                        <i className="bi bi-plus-circle"></i>
                        Add
                        <Ripple />
                      </button>
                    </div>
                  </React.Fragment>
                )}
              </div>

              {showAddedTaskList && (
                <div className="col-12 col-lg-4 ps-lg-4 add_list_divider">
                  <div className="total_time_area">
                    <h6>Total duration :</h6>
                    <span>{displayTotalTime}</span>
                  </div>

                  {addTaskDisable && (
                    <small className="form_error_msg d-block mb-2">
                      Total task duration cannot be more than 10 hours.
                    </small>
                  )}

                  <div className="added_task_list pb-2 pb-sm-3">
                    {createTaskArray.map((item, index) => (
                      <div
                        className="added_task_list_item has_sub"
                        key={item.created_token}
                      >
                        <div className="added_task_list_sub">
                          <button className="added_task is_btn p-ripple text-capitalize">
                            <span>{index + 1}.</span>&nbsp; {item.task_name}
                            <Ripple />
                          </button>

                          <button
                            type="button"
                            className="close_modal_btn small d-flex align-items-center danger p-ripple"
                            onClick={() => handleDeleteAddedTask(index)}
                          >
                            <i className="bi bi-trash3 icon_sm"></i>
                            <Ripple />
                          </button>
                        </div>

                        <div className="task_duration">
                          <small>
                            Duration : {item.hours} Hour(s) {item.minute}{" "}
                            Minute(s)
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      <TaskDetailsDialog
        visible={showTaskViewModal}
        taskData={selectedTaskData}
        onHide={handleCloseTaskViewModal}
        onEdit={handleEditTask}
        canEdit={isPermissionEditTask}
        canTaskCompleted={isPermissionTaskCompleted}
        onComplete={openCompleteTaskPopup}
        onFollowUp={openFollowUpPopup}
        onCarryForward={openCarryForwardPopup}
        onAssign={openAssignTaskPopup}
      />

      <CompleteTaskDialog
        visible={showCompleteTaskDialog}
        taskData={completeTaskData}
        onHide={() => setShowCompleteTaskDialog(false)}
        onSuccess={handleActionSuccess}
        showToast={showToast}
      />

      <FollowUpDialog
        visible={showFollowUpDialog}
        taskData={followUpTaskData}
        onHide={() => setShowFollowUpDialog(false)}
        onSuccess={handleActionSuccess}
        showToast={showToast}
      />

      <CarryForwardDialog
        visible={showCarryForwardDialog}
        taskData={carryForwardTaskData}
        onHide={() => setShowCarryForwardDialog(false)}
        onSuccess={handleActionSuccess}
        showToast={showToast}
      />

      <AssignTaskDialog
        visible={showAssignTaskDialog}
        taskData={assignTaskData}
        contacts={contacts}
        onHide={() => setShowAssignTaskDialog(false)}
        onSuccess={handleActionSuccess}
        showToast={showToast}
      />

      <CompletedEvidenceDialog
        visible={showEvidenceDialog}
        taskData={evidenceTaskData}
        onHide={() => {
          setShowEvidenceDialog(false);
          setEvidenceTaskData(null);
        }}
      />

      <div
        className={`message_backdrop ${showAlert ? "show" : ""}`}
        onClick={handleCloseAlert}
      >
        <div
          className={`message_box ${showAlert ? "show" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="message_box_content">
            <i className="bi bi-check-circle-fill message_box_icon"></i>
            <h6 className="message_box_message">{successMessage}</h6>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Task;